import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  ALLOWANCE_INTERVALS,
  POCKET_GOAL_NAME_MAX,
  parseAmount,
  pocketSummary,
} from '@kraftwerk/shared';
import type { AllowanceInterval } from '@kraftwerk/shared';
import { currentOwnerId } from '../../auth.js';
import { db } from '../../db/local.js';
import { syncOnce } from '../../sync/engine.js';
import type { LocalHouseholdMember, LocalPocketGoal, LocalPocketMoney } from '../../db/local.js';
import {
  addPocketEntry,
  addPocketGoal,
  deletePocketEntry,
  deletePocketGoal,
  pocketEntries,
  pocketGoals,
  setAllowance,
  setTaskRewards,
} from '../../db/pocket.js';
import { useToday } from '../../hooks/useToday.js';
import { formatDay } from '../../format.js';
import { formatMoney } from './Costs.js';
import { StarChart } from './Stars.js';

/**
 * Taschengeld (E20): ein kleines Konto des Kindes. Das Kind trägt ein, was es
 * gekauft hat; die Eltern stellen die Regel ein und geben etwas dazu.
 * Gebucht wird als Zeilen — der Stand ist die Summe, nie ein Zähler.
 */
export function PocketLedger({
  child,
  mode,
  mayWrite,
}: {
  child: LocalHouseholdMember;
  /** `child`: das Kind selbst. `parent`: ein Erwachsener beim Kind. */
  mode: 'child' | 'parent';
  /** Kleine Kinder (E20) sehen ihren Stand, tragen aber noch nichts ein. */
  mayWrite: boolean;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const me = currentOwnerId();
  const entries = useLiveQuery(
    () => pocketEntries(child.userId, db),
    [child.userId],
    [] as LocalPocketMoney[],
  );
  const goals = useLiveQuery(
    () => pocketGoals(child.userId, db),
    [child.userId],
    [] as LocalPocketGoal[],
  );
  const summary = pocketSummary(
    entries,
    goals.map((goal) => goal.id),
  );
  const balance = summary.total;
  const goalName = new Map(goals.map((goal) => [goal.id, goal.name]));

  return (
    <section className="flex flex-col gap-3" aria-label={t('pocket.heading')}>
      <StarChart child={child} mode={mode} />
      <div className="flex flex-col gap-1">
        <p className="kw-label">{t('pocket.balance')}</p>
        <p className={`kw-figure text-4xl ${balance < 0 ? 'text-warn' : ''}`}>
          {formatMoney(balance, i18n.language)}
        </p>
        {goals.length > 0 ? (
          <p className="text-sm">
            {t('pocket.goals.split', {
              free: formatMoney(summary.free, i18n.language),
              saved: formatMoney(summary.total - summary.free, i18n.language),
            })}
          </p>
        ) : null}
        <p className="text-xs text-ink-muted">{allowanceText(child, t, i18n.language)}</p>
      </div>

      {mode === 'parent' ? <AllowanceEditor child={child} /> : null}
      {mode === 'parent' ? <RewardsToggle child={child} /> : null}
      <PocketGoals
        child={child}
        mode={mode}
        mayWrite={mayWrite}
        goals={goals}
        saved={summary.saved}
        free={summary.free}
      />
      {mayWrite ? <EntryForm child={child} mode={mode} /> : null}
      {mode === 'child' && !mayWrite ? (
        <p role="status" className="text-sm text-ink-muted">
          {t('children.level.readOnly')}
        </p>
      ) : null}

      {entries.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('pocket.empty')}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-line">
          {entries.slice(0, 30).map((entry) => (
            <li key={entry.id} className="flex min-h-11 items-center gap-3 py-1">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{entry.note || t(`pocket.kind.${entry.kind}`)}</p>
                <p className="text-xs text-ink-muted">
                  {formatDay(entry.date, 'short', i18n.language)} · {t(`pocket.kind.${entry.kind}`)}
                  {entry.goalId ? ` · ${goalName.get(entry.goalId) ?? t('pocket.goals.gone')}` : ''}
                </p>
              </div>
              <span className={`font-semibold tabular-nums ${amountTone(entry.kind)}`}>
                {amountSign(entry.kind)}
                {formatMoney(entry.amountCents, i18n.language)}
              </span>
              {mine(entry, me) && entry.kind !== 'allowance' && entry.kind !== 'reward' ? (
                <button
                  type="button"
                  onClick={() => void deletePocketEntry(entry, db)}
                  aria-label={t('pocket.removeNamed', {
                    what: entry.note || t(`pocket.kind.${entry.kind}`),
                  })}
                  className="kw-btn kw-btn-quiet kw-btn-small px-3"
                >
                  ×
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Eigene Zeile? Was vom Server kommt, trägt immer `userId`. Eine Zeile ohne
 * kann nur auf diesem Gerät entstanden sein — vom Besitzer, denn ein
 * Kontowechsel leert das Gerät (Einträge vor dem 24.10.2026 trugen keinen).
 */
function mine(row: { userId?: string }, me: string | null): boolean {
  return me !== null && (row.userId ?? me) === me;
}

/** Umbuchungen ändern den Stand nicht — sie stehen ohne Vorzeichen da. */
function amountSign(kind: string): string {
  if (kind === 'expense') return '−';
  if (kind === 'save' || kind === 'withdraw') return '';
  return '+';
}

function amountTone(kind: string): string {
  return kind === 'expense' || kind === 'save' || kind === 'withdraw' ? '' : 'text-good';
}

function allowanceText(child: LocalHouseholdMember, t: TFunction, language: string): string {
  if (!child.allowanceCents || !child.allowanceInterval || !child.allowanceDay) {
    return t('pocket.noRule');
  }
  const amount = formatMoney(child.allowanceCents, language);
  return child.allowanceInterval === 'weekly'
    ? t('pocket.ruleWeekly', { amount, day: t(`pocket.weekday.${child.allowanceDay}`) })
    : t('pocket.ruleMonthly', { amount, day: child.allowanceDay });
}

/** Ausgabe (Kind) bzw. dazugeben oder Ausgabe (Eltern). */
function EntryForm({
  child,
  mode,
}: {
  child: LocalHouseholdMember;
  mode: 'child' | 'parent';
}): React.JSX.Element {
  const { t } = useTranslation();
  const today = useToday();
  const ids = { amount: useId(), note: useId() };
  const [kind, setKind] = useState<'expense' | 'gift'>(mode === 'child' ? 'expense' : 'gift');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const cents = parseAmount(amount);

  return (
    <form
      className="flex flex-col gap-2 rounded-xl bg-surface-raised p-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (cents === null || cents <= 0 || !today) return;
        void addPocketEntry(
          {
            childId: child.userId,
            householdId: child.householdId,
            kind,
            amountCents: cents,
            date: today,
            note: note.trim() || null,
          },
          db,
        ).then(() => {
          setAmount('');
          setNote('');
        });
      }}
    >
      <p className="kw-label">{mode === 'child' ? t('pocket.bought') : t('pocket.addHeading')}</p>
      {mode === 'parent' ? (
        <div className="flex flex-wrap gap-2">
          {(['gift', 'expense'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={kind === option}
              onClick={() => setKind(option)}
              className="kw-toggle"
            >
              {t(`pocket.kind.${option}`)}
            </button>
          ))}
        </div>
      ) : null}
      <div className="flex gap-2">
        <label htmlFor={ids.amount} className="flex w-32 flex-col gap-1">
          <span className="kw-label">{t('pocket.amount')}</span>
          <input
            id={ids.amount}
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="0,00"
            aria-invalid={amount !== '' && cents === null}
            className="kw-field"
          />
        </label>
        <label htmlFor={ids.note} className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="kw-label">{t('pocket.note')}</span>
          <input
            id={ids.note}
            value={note}
            maxLength={200}
            onChange={(event) => setNote(event.target.value)}
            className="kw-field"
          />
        </label>
      </div>
      {amount !== '' && cents === null ? (
        <p role="alert" className="text-xs text-danger">
          {t('costs.invalidAmount')}
        </p>
      ) : null}
      <button type="submit" disabled={cents === null} className="kw-btn kw-btn-small">
        {t('pocket.save')}
      </button>
    </form>
  );
}

/** Regel der Eltern: Betrag, wöchentlich oder monatlich, an welchem Tag. */
function AllowanceEditor({ child }: { child: LocalHouseholdMember }): React.JSX.Element {
  const { t } = useTranslation();
  const ids = { amount: useId(), day: useId() };
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(
    child.allowanceCents ? (child.allowanceCents / 100).toFixed(2).replace('.', ',') : '',
  );
  const [interval, setPeriod] = useState<AllowanceInterval>(
    child.allowanceInterval === 'monthly' ? 'monthly' : 'weekly',
  );
  const [day, setDay] = useState(child.allowanceDay ?? 6);
  const [error, setError] = useState<string | null>(null);
  const cents = parseAmount(amount);
  const days =
    interval === 'weekly' ? [1, 2, 3, 4, 5, 6, 7] : Array.from({ length: 28 }, (_, i) => i + 1);

  const save = (rule: Parameters<typeof setAllowance>[1]) => {
    setError(null);
    setAllowance(child.userId, rule)
      .then(() => setOpen(false))
      .catch(() => setError(t('children.offline')));
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="kw-btn kw-btn-quiet kw-btn-small self-start px-3"
      >
        {child.allowanceCents ? t('pocket.changeRule') : t('pocket.setRule')}
      </button>
    );
  }

  return (
    <form
      className="flex flex-col gap-2 rounded-xl bg-surface-raised p-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (cents === null || cents <= 0) return;
        save({ amountCents: cents, interval, day: Math.min(day, interval === 'weekly' ? 7 : 28) });
      }}
    >
      <p className="kw-label">{t('pocket.ruleHeading')}</p>
      <div className="flex flex-wrap gap-2">
        {ALLOWANCE_INTERVALS.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={interval === option}
            onClick={() => {
              setPeriod(option);
              setDay(option === 'weekly' ? 6 : 1);
            }}
            className="kw-toggle"
          >
            {t(`pocket.interval.${option}`)}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <label htmlFor={ids.amount} className="flex w-32 flex-col gap-1">
          <span className="kw-label">{t('pocket.amount')}</span>
          <input
            id={ids.amount}
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="0,00"
            aria-invalid={amount !== '' && cents === null}
            className="kw-field"
          />
        </label>
        <label htmlFor={ids.day} className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="kw-label">{t('pocket.day')}</span>
          <select
            id={ids.day}
            value={day}
            onChange={(event) => setDay(Number(event.target.value))}
            className="kw-field"
          >
            {days.map((value) => (
              <option key={value} value={value}>
                {interval === 'weekly'
                  ? t(`pocket.weekday.${value}`)
                  : t('pocket.monthDay', { day: value })}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="text-xs text-ink-muted">{t('pocket.ruleHint')}</p>
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={cents === null} className="kw-btn kw-btn-small">
          {t('pocket.saveRule')}
        </button>
        {child.allowanceCents ? (
          <button
            type="button"
            onClick={() => save(null)}
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {t('pocket.endRule')}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('pocket.cancel')}
        </button>
      </div>
    </form>
  );
}

/**
 * Sparziele (24.10.2026): „Fahrrad, 120 €“. Das Kind legt vom freien Geld
 * zurück oder holt zurück, Eltern geben direkt dazu. Gespartes steht als
 * Buchung da, nie als Zähler am Ziel.
 */
function PocketGoals({
  child,
  mode,
  mayWrite,
  goals,
  saved,
  free,
}: {
  child: LocalHouseholdMember;
  mode: 'child' | 'parent';
  mayWrite: boolean;
  goals: LocalPocketGoal[];
  saved: Map<string, number>;
  free: number;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const canAdd = mode === 'parent' || mayWrite;
  if (goals.length === 0 && !canAdd) return null;

  return (
    <div className="flex flex-col gap-2">
      <p className="kw-label">{t('pocket.goals.heading')}</p>
      {goals.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('pocket.goals.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {goals.map((goal) => (
            <GoalRow
              key={goal.id}
              child={child}
              goal={goal}
              saved={saved.get(goal.id) ?? 0}
              free={free}
              mode={mode}
              mayWrite={mayWrite}
            />
          ))}
        </ul>
      )}
      {canAdd ? (
        adding ? (
          <GoalForm child={child} onDone={() => setAdding(false)} />
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="kw-btn kw-btn-quiet kw-btn-small self-start px-3"
          >
            {t('pocket.goals.add')}
          </button>
        )
      ) : null}
    </div>
  );
}

function GoalRow({
  child,
  goal,
  saved,
  free,
  mode,
  mayWrite,
}: {
  child: LocalHouseholdMember;
  goal: LocalPocketGoal;
  saved: number;
  free: number;
  mode: 'child' | 'parent';
  mayWrite: boolean;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const today = useToday();
  const me = currentOwnerId();
  const amountId = useId();
  const [action, setAction] = useState<'save' | 'withdraw' | 'gift' | null>(null);
  const [amount, setAmount] = useState('');
  const cents = parseAmount(amount);
  const reached = saved >= goal.targetCents;
  const percent = Math.max(0, Math.min(100, (saved / goal.targetCents) * 100));
  // Mehr zurücklegen als frei ist oder mehr zurückholen als gespart: das
  // ergäbe einen Stand, den es nicht gibt.
  const limit = action === 'save' ? free : action === 'withdraw' ? saved : null;
  const tooMuch = cents !== null && limit !== null && cents > limit;
  const actions: ('save' | 'withdraw' | 'gift')[] =
    mode === 'parent' ? ['gift'] : mayWrite ? ['save', 'withdraw'] : [];

  return (
    <li className="flex flex-col gap-2 rounded-xl bg-surface-raised p-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium">{goal.name}</p>
          <p className={`text-sm tabular-nums ${reached ? 'text-good' : ''}`}>
            {t('pocket.goals.progress', {
              saved: formatMoney(saved, i18n.language),
              target: formatMoney(goal.targetCents, i18n.language),
            })}
            {' · '}
            {reached
              ? t('pocket.goals.reached')
              : t('pocket.goals.missing', {
                  amount: formatMoney(goal.targetCents - saved, i18n.language),
                })}
          </p>
        </div>
        {mine(goal, me) ? (
          <button
            type="button"
            onClick={() => void deletePocketGoal(goal, db)}
            aria-label={t('pocket.goals.removeNamed', { name: goal.name })}
            className="kw-btn kw-btn-quiet kw-btn-small px-3"
          >
            ×
          </button>
        ) : null}
      </div>
      <span
        className="kw-bar kw-bar-thin"
        role="progressbar"
        aria-label={goal.name}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(percent)}
      >
        <i
          style={{
            width: `${percent}%`,
            backgroundColor: reached ? 'var(--color-good)' : undefined,
          }}
        />
      </span>
      {action === null ? (
        actions.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {actions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  setAction(option);
                  setAmount('');
                }}
                disabled={option === 'withdraw' && saved <= 0}
                className="kw-btn kw-btn-quiet kw-btn-small px-3"
              >
                {t(`pocket.goals.action.${option}`)}
              </button>
            ))}
          </div>
        ) : null
      ) : (
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (cents === null || cents <= 0 || tooMuch || !today) return;
            void addPocketEntry(
              {
                childId: child.userId,
                householdId: child.householdId,
                kind: action,
                amountCents: cents,
                goalId: goal.id,
                date: today,
                note: null,
              },
              db,
            ).then(() => setAction(null));
          }}
        >
          <label htmlFor={amountId} className="flex w-40 flex-col gap-1">
            <span className="kw-label">{t(`pocket.goals.action.${action}`)}</span>
            <input
              id={amountId}
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="0,00"
              aria-invalid={(amount !== '' && cents === null) || tooMuch}
              className="kw-field"
            />
          </label>
          {amount !== '' && cents === null ? (
            <p role="alert" className="text-xs text-danger">
              {t('costs.invalidAmount')}
            </p>
          ) : null}
          {tooMuch && limit !== null ? (
            <p role="alert" className="text-xs text-warn">
              {t(action === 'save' ? 'pocket.goals.tooMuchSave' : 'pocket.goals.tooMuchWithdraw', {
                amount: formatMoney(Math.max(0, limit), i18n.language),
              })}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={cents === null || cents <= 0 || tooMuch}
              className="kw-btn kw-btn-small"
            >
              {t('pocket.save')}
            </button>
            <button
              type="button"
              onClick={() => setAction(null)}
              className="kw-btn kw-btn-quiet kw-btn-small"
            >
              {t('pocket.cancel')}
            </button>
          </div>
        </form>
      )}
    </li>
  );
}

function GoalForm({
  child,
  onDone,
}: {
  child: LocalHouseholdMember;
  onDone: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const ids = { name: useId(), target: useId() };
  const [name, setName] = useState('');
  const [target, setTarget] = useState('');
  const cents = parseAmount(target);
  const valid = name.trim() !== '' && cents !== null && cents > 0;

  return (
    <form
      className="flex flex-col gap-2 rounded-xl bg-surface-raised p-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid || cents === null) return;
        void addPocketGoal(
          {
            childId: child.userId,
            householdId: child.householdId,
            name: name.trim(),
            targetCents: cents,
          },
          db,
        ).then(onDone);
      }}
    >
      <div className="flex gap-2">
        <label htmlFor={ids.name} className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="kw-label">{t('pocket.goals.name')}</span>
          <input
            id={ids.name}
            value={name}
            maxLength={POCKET_GOAL_NAME_MAX}
            onChange={(event) => setName(event.target.value)}
            className="kw-field"
          />
        </label>
        <label htmlFor={ids.target} className="flex w-32 flex-col gap-1">
          <span className="kw-label">{t('pocket.goals.target')}</span>
          <input
            id={ids.target}
            inputMode="decimal"
            value={target}
            onChange={(event) => setTarget(event.target.value)}
            placeholder="0,00"
            aria-invalid={target !== '' && cents === null}
            className="kw-field"
          />
        </label>
      </div>
      {target !== '' && cents === null ? (
        <p role="alert" className="text-xs text-danger">
          {t('costs.invalidAmount')}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={!valid} className="kw-btn kw-btn-small">
          {t('pocket.goals.create')}
        </button>
        <button type="button" onClick={onDone} className="kw-btn kw-btn-quiet kw-btn-small">
          {t('pocket.cancel')}
        </button>
      </div>
    </form>
  );
}

/** Geld für erledigte Aufgaben (E20): ab Werk aus, schalten nur die Eltern an. */
function RewardsToggle({ child }: { child: LocalHouseholdMember }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const on = child.taskRewards === true;

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        aria-pressed={on}
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setError(null);
          setTaskRewards(child.userId, !on)
            // Die Mitgliedschaft schreibt der Server — erst der Abgleich zeigt den neuen Stand.
            .then(() => syncOnce())
            .catch(() => setError(t('children.offline')))
            .finally(() => setBusy(false));
        }}
        className="kw-toggle self-start"
      >
        {t('pocket.rewards.toggle')}
      </button>
      <p className="text-xs text-ink-muted">
        {on && child.rewardsSince
          ? t('pocket.rewards.on', {
              date: formatDay(child.rewardsSince.slice(0, 10), 'short', i18n.language),
            })
          : t('pocket.rewards.off', { name: child.displayName })}
      </p>
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
